// Axios client for /api/livepreview/* endpoints.

import api from '@/api/client'
import type {
  DeleteResponse, DeployResponse, EmbedBatchOutcome, EmbedBatchRecordInput,
  EmbedRetrieveResponse, FixCodeResponse, ListResponse, LoadResponse,
  LookupResponse, RedeployFunctionResponse, ResetResponse, SaveResponse,
  RunActionResponse,
} from './types'
import type { WizardVia } from '@twasta/page-runtime/wizard/contract'

/**
 * A request made BY a Transaction Wizard (obj_type "U") carries the wizard it
 * runs under beside `payload` in the body — never inside it. The server then
 * authorizes it against the WIZARD's own grant (run / add / edit) instead of
 * the base transaction's, and bounds a save with the write guard (design
 * §12.2). Absent on every ordinary call, so those bodies are unchanged.
 */
export interface WizardCallOpts {
  wizard?: WizardVia
}

const viaBody = (opts?: WizardCallOpts) => (opts?.wizard ? { wizard: opts.wizard } : {})

export async function livePreviewDeploy(
  project: string,
  objName: string,
): Promise<DeployResponse> {
  const res = await api.post('/livepreview/deploy', {
    project,
    obj_name: objName,
  })
  return res.data
}

/**
 * Resolve project-variable defaults for every column in this
 * transaction so the Add form can pre-fill them. Audit tokens are
 * server-stamped at INSERT and not included in the response.
 */
export async function livePreviewResolveDefaults(
  project: string,
  objName: string,
  opts?: WizardCallOpts,
): Promise<{ ok: boolean; values: Record<string, unknown>; protect?: Record<string, 0 | 1> }> {
  const res = await api.post('/livepreview/resolve-defaults', {
    project,
    obj_name: objName,
    ...viaBody(opts),
  })
  return res.data
}

/**
 * Answer one chatbot-block turn in the in-app preview. Grounds on the
 * page text (extracted client-side) + a bounded sample of records from
 * the configured objects, using the author's twasta LLM settings.
 */
export interface PreviewChatTurn { role: 'user' | 'assistant'; content: string }

export async function livePreviewChat(
  project: string,
  body: {
    messages: PreviewChatTurn[]
    page_text?: string
    obj_names?: string[]
    system_prompt?: string
  },
): Promise<{ reply: string; used_objects: string[] }> {
  const res = await api.post('/livepreview/chat', { project, ...body })
  return res.data
}

import type {
  AppNotification, NotificationPrefs, PrincipalDirectory,
  TaskSeed, WorkTask, WorklistCounts, WorklistPage,
} from '@twasta/page-runtime/shell'

export async function livePreviewExecute(
  project: string,
  objName: string,
  action: string,
  payload: Record<string, unknown> = {},
  opts?: WizardCallOpts,
): Promise<unknown> {
  const res = await api.post('/livepreview/execute', {
    project,
    obj_name: objName,
    action,
    payload,
    ...viaBody(opts),
  })
  return res.data
}

// ── Per-tenant model overlays (Customize) ────────────────────────────────

export interface ModelOverlayResult {
  obj_type: string
  obj_name: string
  core: Record<string, unknown>
  overlay: Record<string, unknown>
  effective: Record<string, unknown>
  version: number
}

// One leaf change: `path` is a dotted overlay path (uses `@items`/<key> for
// keyed arrays), `key` names the base field the array keys match (e.g. col_id).
// The type now lives in the shared package (single source — the shared
// useGridLayout hook emits these leaves); re-exported here so existing
// importers keep working.
import type { OverlaySetLeaf } from '@twasta/page-runtime/txn/list'
export type { OverlaySetLeaf } from '@twasta/page-runtime/txn/list'

export async function modelOverlayGet(
  project: string, objName: string, objType = 'T',
): Promise<ModelOverlayResult> {
  const res = await api.post('/livepreview/model-overlay/get', {
    project, obj_name: objName, obj_type: objType,
  })
  return res.data
}

export async function modelOverlayPatch(
  project: string, objName: string,
  set: OverlaySetLeaf[] = [], reset: string[] = [], objType = 'T',
): Promise<{ ok: boolean; overlay: Record<string, unknown> }> {
  const res = await api.post('/livepreview/model-overlay/patch', {
    project, obj_name: objName, obj_type: objType, set, reset,
  })
  return res.data
}

/** Whether the caller may customize this application's screens for their
 *  organization (owner / builders, or the tenant's functional admin). */
export async function modelOverlayCanCustomize(
  project: string, objType = 'T',
): Promise<boolean> {
  try {
    const res = await api.post('/livepreview/model-overlay/can-customize', {
      project, obj_type: objType,
    })
    return !!res.data?.can_customize
  } catch {
    return false
  }
}

export async function modelOverlayReset(
  project: string, objName: string, objType = 'T',
): Promise<{ ok: boolean }> {
  const res = await api.post('/livepreview/model-overlay/reset', {
    project, obj_name: objName, obj_type: objType,
  })
  return res.data
}

// Body + result for an atomic multi-form save + the batch-scope error locator.
// Single-sourced from the shared screen contract (these were byte-identical
// wire twins); re-exported so existing `import { BatchSaveBody } from
// '@/livepreview/api'` consumers keep working unchanged.
import type {
  BatchSaveBody, BatchSaveResult, BatchScope,
} from '@twasta/page-runtime/txn/screen'
export type {
  BatchSaveBody, BatchSaveResult, BatchScope,
} from '@twasta/page-runtime/txn/screen'

export async function livePreviewExecuteBatch(
  project: string, objName: string, body: BatchSaveBody, opts?: WizardCallOpts,
): Promise<BatchSaveResult> {
  // The idempotency key rides the `X-Idempotency-Key` header (the engine
  // replays the first save for a repeated key — a wizard's double tap); it is
  // never part of the body the engine reads.
  const res = await api.post('/livepreview/execute-batch', {
    project,
    obj_name: objName,
    header: body.header,
    details: body.details,
    ...(body.txn_id ? { txn_id: body.txn_id } : {}),
    ...(body.apply_item_change ? { apply_item_change: true } : {}),
    ...(body.ignore_warnings?.length ? { ignore_warnings: body.ignore_warnings } : {}),
    ...(body.reason ? { reason: body.reason } : {}),
    ...(body.reason_code ? { reason_code: body.reason_code } : {}),
    ...(body.expect && Object.keys(body.expect).length ? { expect: body.expect } : {}),
    ...viaBody(opts),
  }, body.idempotency_key ? { headers: { 'X-Idempotency-Key': body.idempotency_key } } : undefined)
  return res.data
}

// The list-request wire types are single-sourced from the shared package
// (byte-identical twins): the resolved filter clause + the enforced-condition
// overrides live in the screen contract, the active filter-set selection in
// the capture contract. Re-exported so existing `import { ListFilter } from
// '@/livepreview/api'` consumers keep working unchanged.
import type { ListFilter, EnforcedOverrides } from '@twasta/page-runtime/txn/screen'
export type { ListFilter, EnforcedOverrides } from '@twasta/page-runtime/txn/screen'
import type { FilterSetSelection } from '@twasta/page-runtime/txn/capture'
export type { FilterSetSelection } from '@twasta/page-runtime/txn/capture'

export async function livePreviewList(
  project: string,
  objName: string,
  limit = 50,
  offset = 0,
  formNo?: string,
  parentPk?: Record<string, unknown>,
  filters?: ListFilter[],
  enforcedOverrides?: EnforcedOverrides,
  filterSet?: FilterSetSelection,
  /** `limit` is a size the end user picked, not this caller's default — the
   *  engine then keeps it instead of substituting the object's configured
   *  list page size. See ListQuery.explicitLimit. */
  explicitLimit?: boolean,
  /** The list TAB to show (`list_view.tabs`) and whether to count every tab. */
  tabSel?: { tab?: string; withTabCounts?: boolean },
): Promise<ListResponse> {
  const params: Record<string, unknown> = {
    project, obj_name: objName, limit, offset,
  }
  if (explicitLimit) params.explicit_limit = true
  if (tabSel?.tab) params.tab = tabSel.tab
  if (tabSel?.withTabCounts) params.with_tab_counts = true
  if (formNo) params.form_no = formNo
  if (parentPk && Object.keys(parentPk).length > 0) {
    params.parent_pk = JSON.stringify(parentPk)
  }
  if (filters && filters.length > 0) {
    params.filters = JSON.stringify(filters)
  }
  if (enforcedOverrides && Object.keys(enforcedOverrides).length > 0) {
    params.enforced_overrides = JSON.stringify(enforcedOverrides)
  }
  if (filterSet?.bypass) {
    params.bypass_enforced = true
  } else if (filterSet?.filterSetId) {
    params.enforced_filter_set_id = filterSet.filterSetId
  }
  const res = await api.get('/livepreview/records', { params })
  return res.data
}

/** What the current user may do to `objName` — drives the screen's
 *  affordances (the endpoints still gate). Same shape the deployed runtime's
 *  `/rights` returns, so the shared screen consumes either. */
export async function livePreviewRights(
  project: string,
  objName: string,
): Promise<{ can_read: boolean; actions: string[]; buttons?: { restricted: string[]; granted: string[] } }> {
  const res = await api.get('/livepreview/rights', { params: { project, obj_name: objName } })
  return res.data
}

export async function livePreviewLoad(
  project: string,
  objName: string,
  txnId: string,
): Promise<LoadResponse> {
  const res = await api.get(`/livepreview/record/${encodeURIComponent(txnId)}`, {
    params: { project, obj_name: objName },
  })
  return res.data
}

/**
 * Upload an image placed inside a RICHTEXT column. Stored in the project's
 * page-asset library and returned as its PUBLIC URL, because the same body is
 * rendered on published pages. Gated server-side on the add/edit right for
 * `objName`.
 */
export async function livePreviewUploadContentImage(
  project: string,
  objName: string,
  file: File,
): Promise<{ url: string; name?: string }> {
  const fd = new FormData()
  fd.append('project', project)
  fd.append('obj_name', objName)
  fd.append('file', file)
  const res = await api.post('/livepreview/content-image', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  if (!res.data?.url) throw new Error('The server did not return an image address')
  return { url: res.data.url, name: file.name }
}

export async function livePreviewSave(
  project: string,
  objName: string,
  header: Record<string, unknown>,
  txnId?: string,
  opts?: { ignoreWarnings?: string[] } & WizardCallOpts,
): Promise<SaveResponse> {
  return await livePreviewExecute(project, objName, 'save', {
    header,
    txn_id: txnId,
    ...(opts?.ignoreWarnings?.length ? { ignore_warnings: opts.ignoreWarnings } : {}),
  }, opts) as SaveResponse
}

/**
 * Validate ONE form's payload on demand, without writing — running the SAME
 * checks the save runs for that form (mandatory fields + field-level rules +
 * form-level rules), so navigate-away validation matches what blocks a save.
 * Resolves on success; throws a 422 (parseApiError → details.errors) on
 * failure so the caller can surface the messages and block the navigation.
 *
 * `txnId` — the record this payload belongs to — is REQUIRED for the same
 * reason as in `executeValidate`: it is what makes the engine validate an edit
 * rather than an insert, and omitting it makes record-aware rules like
 * `must_be_unique` treat the record's own stored row as a foreign duplicate.
 * Pass `undefined` only for a genuinely new record.
 */
export async function livePreviewValidate(
  project: string,
  objName: string,
  formNo: string | undefined,
  header: Record<string, unknown>,
  opts: {
    headerValues?: Record<string, unknown>
    parentPk?: Record<string, unknown>
    txnId: string | undefined
  },
): Promise<void> {
  const { headerValues, parentPk, txnId } = opts
  await livePreviewExecute(project, objName, 'validate', {
    ...(formNo ? { form_no: formNo } : {}),
    header,
    header_values: headerValues || {},
    form_rules_only: false,
    ...(parentPk && Object.keys(parentPk).length > 0 ? { parent_pk: parentPk } : {}),
    ...(txnId ? { txn_id: txnId } : {}),
  })
}

// Run a custom action button. The engine runs its pre-action validations
// first; a failure throws (the error envelope carries the messages so the
// caller can surface them and block the action). On success it resolves with
// a RunActionResponse describing the handler result.
export async function livePreviewRunAction(
  project: string,
  objName: string,
  req: {
    formNo: string
    actionName: string
    header: Record<string, unknown>
    headerValues?: Record<string, unknown>
    txnId?: string
    parentPk?: Record<string, unknown>
    ignoreWarnings?: string[]
    confirm?: { acknowledged?: boolean; answer?: string; resend?: boolean }
    rowIsNew?: boolean
    lineCount?: number
    selectedRows?: Array<Record<string, unknown>>
  },
  opts?: WizardCallOpts,
): Promise<RunActionResponse> {
  return await livePreviewExecute(project, objName, 'run_action', {
    form_no: req.formNo,
    action_name: req.actionName,
    header: req.header,
    header_values: req.headerValues || {},
    ...(req.txnId ? { txn_id: req.txnId } : {}),
    ...(req.parentPk && Object.keys(req.parentPk).length > 0 ? { parent_pk: req.parentPk } : {}),
    ...(req.ignoreWarnings?.length ? { ignore_warnings: req.ignoreWarnings } : {}),
    ...(req.confirm ? { confirm: req.confirm } : {}),
    ...(req.rowIsNew !== undefined ? { row_is_new: req.rowIsNew } : {}),
    ...(req.lineCount !== undefined ? { line_count: req.lineCount } : {}),
    ...(req.selectedRows ? { selected_rows: req.selectedRows } : {}),
  }, opts) as RunActionResponse
}

export async function livePreviewDelete(
  project: string,
  objName: string,
  txnId: string,
): Promise<DeleteResponse> {
  return await livePreviewExecute(project, objName, 'delete', {
    txn_id: txnId,
  }) as DeleteResponse
}

// ── Audit trail (record change-history) ─────────────────────────────────
// Shape mirrors `record_history` entries; `AuditEntry` from
// @twasta/page-runtime renders them via <AuditTimeline>.
import type { AuditEntry } from '@twasta/page-runtime'

export async function livePreviewRecordHistory(
  project: string,
  objName: string,
  txnId: string,
  limit = 100,
  offset = 0,
): Promise<{ ok: boolean; entries: AuditEntry[] }> {
  const res = await api.get(
    `/livepreview/history/${encodeURIComponent(objName)}/${encodeURIComponent(txnId)}`,
    { params: { project, limit, offset } },
  )
  return res.data
}

export interface PreviewAuditFilters {
  obj_name?: string
  action?: string
  user_id?: string
  /** Interface the change came through: web / assistant / integration / … */
  client?: string
  date_from?: string
  date_to?: string
  limit?: number
  offset?: number
}

export async function livePreviewAuditLog(
  project: string,
  filters: PreviewAuditFilters = {},
): Promise<{ ok: boolean; entries: AuditEntry[] }> {
  const res = await api.get('/livepreview/audit', {
    params: { project, ...filters },
  })
  return res.data
}

export async function livePreviewReset(
  project: string,
  objName?: string,
): Promise<ResetResponse> {
  const res = await api.post('/livepreview/reset', {
    project,
    obj_name: objName,
  })
  return res.data
}

export async function livePreviewLookup(
  project: string,
  objName: string,
  lookup: string,
  search = '',
  limit = 50,
  opts?: {
    filter?: string
    formValues?: Record<string, unknown>
    headerValues?: Record<string, unknown>
    literalValues?: Record<string, unknown>
    lookupSql?: string
    lookupSqlDataCol?: string
    lookupField?: string
    lookupInheritedField?: string
  } & WizardCallOpts,
): Promise<LookupResponse> {
  const res = await api.post('/livepreview/lookup', {
    project, obj_name: objName, lookup, search, limit,
    ...viaBody(opts),
    filter: opts?.filter,
    form_values: opts?.formValues,
    header_values: opts?.headerValues,
    literal_values: opts?.literalValues,
    lookup_sql: opts?.lookupSql,
    lookup_sql_data_col: opts?.lookupSqlDataCol,
    lookup_field: opts?.lookupField,
    lookup_inherited_field: opts?.lookupInheritedField,
  })
  return res.data
}

/**
 * List the tables that physically exist in a project's preview schema.
 * Used by the lookup designer's table picker — these are the tables a
 * lookup can actually target (deployed tables), which the Database-Designer
 * JSON catalog may not include.
 */
export async function livePreviewTables(
  project: string,
): Promise<{ ok: true; tables: { name: string }[] }> {
  const res = await api.post('/livepreview/tables', { project })
  return res.data
}

/**
 * List the columns of one preview-schema table. Used by the lookup
 * designer's display/value column pickers — these reflect the deployed
 * table's actual columns, which the Database-Designer JSON may not have.
 */
export async function livePreviewColumns(
  project: string,
  table: string,
): Promise<{ ok: true; columns: { column_name: string; data_type?: string }[] }> {
  const res = await api.post('/livepreview/columns', { project, table })
  return res.data
}

/**
 * Resolve the transaction whose Add form the lookup picker opens. Used by
 * the LookupModal's "+ Add" flow so the user can add a new master row
 * without leaving the form they're editing. Prefers an explicit
 * ``objName`` (the configured "add record" object); otherwise resolves the
 * owning transaction from ``table``. Returns the transaction's obj_name +
 * full metadata payload (same shape as the load-transaction-object
 * endpoint) so the front-end can render a FormRenderer for it directly.
 */
export async function livePreviewLookupSource(
  project: string,
  opts: { table?: string; objName?: string },
): Promise<{ ok: true; obj_name: string; transaction: unknown }> {
  const res = await api.post('/livepreview/lookup-source', {
    project, table: opts.table, obj_name: opts.objName,
  })
  return res.data
}

/**
 * List the project's transaction objects — used by the lookup designer's
 * "add record" object picker. Returns each object's obj_name plus a
 * friendly title and its main table.
 */
export async function livePreviewTransactions(
  project: string,
): Promise<{
  ok: true
  transactions: { obj_name: string; title: string; main_table: string }[]
}> {
  const res = await api.post('/livepreview/transactions', { project })
  return res.data
}

export async function livePreviewFixCode(
  project: string,
  objName: string,
  functionName: string,
  source: string,
  error: string,
): Promise<FixCodeResponse> {
  const res = await api.post('/livepreview/fix-code', {
    project,
    obj_name: objName,
    function_name: functionName,
    source,
    error,
  })
  return res.data
}

// ── Capture / Integrate batch process-transaction-request ───────────
//
// These wrap the same POST/GET endpoints the legacy capture-mode embed
// already hits, but with ``project`` in the payload so the backend
// persists through the live-preview executor (real INSERT/UPDATE + real
// validation) instead of the in-memory stub.

/**
 * Persist a batch of capture/integrate records in a single request.
 * Returns the per-record outcome (with ``validationErrors[]`` for any
 * failures). The wire path is
 * ``POST /proteus-vision-ecom-ext/process-transaction-request`` —
 * routed to the live-preview transaction_request module because the
 * payload carries ``project``.
 */
export async function livePreviewProcess(
  project: string,
  objName: string,
  records: EmbedBatchRecordInput[],
  opts: { documentId?: string } = {},
): Promise<EmbedBatchOutcome> {
  // ``baseURL: '/'`` override — the proteus-vision-ecom-ext router is
  // mounted at the site root (no ``/api`` prefix), unlike the regular
  // backend routes. Without the override axios would resolve this to
  // ``/api/proteus-vision-ecom-ext/...`` which 404/405s.
  const res = await api.post(
    '/proteus-vision-ecom-ext/process-transaction-request',
    {
      project,
      obj_name: objName,
      records,
      ...(opts.documentId ? { documentId: opts.documentId } : {}),
    },
    { baseURL: '/' },
  )
  return res.data
}

/**
 * Fetch one previously-saved capture/integrate record by primary key.
 * The wire path is
 * ``GET /proteus-vision-ecom-ext/process-transaction-request/{obj}/{txn}?project=…``
 * — routed to the live-preview transaction_request module because the
 * query string carries ``project``.
 *
 * Returns ``null`` on 404 (record not found) so callers can render a
 * "not found" affordance without try/catching every retrieve.
 */
export async function livePreviewRetrieve(
  project: string,
  objName: string,
  txnId: string,
): Promise<EmbedRetrieveResponse | null> {
  try {
    const res = await api.get(
      `/proteus-vision-ecom-ext/process-transaction-request/${encodeURIComponent(objName)}/${encodeURIComponent(txnId)}`,
      { params: { project }, baseURL: '/' },
    )
    return res.data
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status
    if (status === 404) return null
    throw err
  }
}

export async function livePreviewRedeployFunction(
  project: string,
  objName: string,
  functionName: string,
  source: string,
): Promise<RedeployFunctionResponse> {
  const res = await api.post('/livepreview/redeploy-function', {
    project,
    obj_name: objName,
    function_name: functionName,
    source,
  })
  return res.data
}

// ── Approvals (multi-level, rule-based) ─────────────────────────────────
// The approval action family rides the same /livepreview/execute endpoint as
// CRUD (the engine resolves the tenant DB directly, so obj_name is only used
// for logging on the cross-object inbox). publish/versions have dedicated
// endpoints. See docs/transaction-approval-workflow-plan.md.

// The approval wire types (instances, envelopes, analytics, delegation, …)
// are SHARED with the standalone runtime client — single source in
// @twasta/page-runtime/txn contracts. Re-exported here so existing
// `import { ApprovalInstance } from '@/livepreview/api'` consumers keep working.
import type {
  ApprovalInstance,
  ApprovalListResponse,
  ApprovalOverride,
} from '@twasta/page-runtime/txn'
export type {
  ApprovalAnalytics,
  ApprovalBulkResult,
  ApprovalChange,
  ApprovalChildChange,
  ApprovalChildRowChange,
  ApprovalDelegation,
  ApprovalInstance,
  ApprovalListResponse,
  ApprovalOverride,
  ApprovalResponse,
  ApprovalSla,
  ApprovalSlaScanResult,
} from '@twasta/page-runtime/txn'

export interface ApprovalVersionMeta {
  version: number
  checksum: string
  profile: string
  status: 'active' | 'superseded' | 'archived'
  published_by?: string | null
  published_at?: string | null
  notes?: string | null
  in_flight: number
}

/** The inbox — instances awaiting the current user, or (super-approvers only,
 *  with `all: true`) every pending approval in the project. */
// ── Worklist ─────────────────────────────────────────────────────────────
//
// The unified "what is waiting on me" list. Rides the same execute door the
// approval family uses, so no new route is needed and the downloaded runtime
// reaches it the same way. `_worklist` is a placeholder object name: the
// worklist_* family resolves the tenant engine directly and never loads a
// specific object's metadata.

export async function worklistList(
  project: string,
  opts: {
    status?: string; kinds?: string[]; limit?: number; offset?: number
    /** Administrator view: everyone's open work (server-gated, like
     *  `approvalList({ all: true })`). */
    all?: boolean
  } = {},
): Promise<WorklistPage> {
  return await livePreviewExecute(project, '_worklist', 'worklist_list', {
    status: opts.status || 'open',
    ...(opts.kinds ? { kinds: opts.kinds } : {}),
    ...(opts.all ? { all: true } : {}),
    limit: opts.limit ?? 200,
    offset: opts.offset ?? 0,
  }) as WorklistPage
}

export async function worklistCount(project: string): Promise<WorklistCounts> {
  return await livePreviewExecute(project, '_worklist', 'worklist_count', {}) as WorklistCounts
}

export async function worklistSeed(project: string, taskKey: string): Promise<TaskSeed> {
  return await livePreviewExecute(project, '_worklist', 'worklist_seed',
    { task_key: taskKey }) as TaskSeed
}

export async function worklistComplete(
  project: string, taskKey: string,
): Promise<{ ok: boolean; closed: boolean }> {
  return await livePreviewExecute(project, '_worklist', 'worklist_complete',
    { task_key: taskKey }) as { ok: boolean; closed: boolean }
}

export async function worklistCreate(
  project: string, input: Record<string, unknown>,
): Promise<WorkTask> {
  return await livePreviewExecute(project, '_worklist', 'worklist_create', input) as WorkTask
}

/** Who this app can hand work to — its roles and the people who hold them. */
export async function worklistPrincipals(
  project: string, q?: string,
): Promise<PrincipalDirectory> {
  return await livePreviewExecute(project, '_worklist', 'worklist_principals',
    q ? { q } : {}) as PrincipalDirectory
}

// ── Notifications ────────────────────────────────────────────────────────
//
// The bell. Same execute door, same reason as the worklist — and deliberately
// a SEPARATE store: a task closes when it is done, a notification when it is
// read, so one badge each.

export async function notificationsList(
  project: string, opts: { unreadOnly?: boolean; limit?: number } = {},
): Promise<{ items: AppNotification[]; unread: number }> {
  return await livePreviewExecute(project, '_notifications', 'notifications_list', {
    ...(opts.unreadOnly ? { unread_only: true } : {}),
    limit: opts.limit ?? 50,
  }) as { items: AppNotification[]; unread: number }
}

export async function notificationsCount(project: string): Promise<{ unread: number }> {
  return await livePreviewExecute(project, '_notifications', 'notifications_count',
    {}) as { unread: number }
}

export async function notificationsMarkRead(
  project: string, ids?: string[],
): Promise<{ marked: number }> {
  return await livePreviewExecute(project, '_notifications', 'notifications_mark_read',
    ids && ids.length ? { notif_ids: ids } : {}) as { marked: number }
}

export async function notificationPrefs(project: string): Promise<NotificationPrefs> {
  return await livePreviewExecute(project, '_notifications', 'notifications_prefs_get',
    {}) as NotificationPrefs
}

export async function saveNotificationPrefs(
  project: string, prefs: NotificationPrefs,
): Promise<NotificationPrefs> {
  return await livePreviewExecute(project, '_notifications', 'notifications_prefs_set',
    { prefs }) as NotificationPrefs
}

export async function approvalList(
  project: string,
  opts: { status?: string; objName?: string; all?: boolean; limit?: number; offset?: number } = {},
): Promise<ApprovalListResponse> {
  return await livePreviewExecute(project, opts.objName || '_approvals', 'approval_list', {
    status: opts.status || 'pending',
    ...(opts.objName ? { obj_name: opts.objName } : {}),
    ...(opts.all ? { all: true } : {}),
    limit: opts.limit ?? 100,
    offset: opts.offset ?? 0,
  }) as ApprovalListResponse
}

/** Publish an immutable version of an object's approval workflow (idempotent
 *  on the semantic checksum). Linear is rebuilt from the levels; pass `xpdl`
 *  for an advanced (graphical) workflow. */
export async function approvalsPublish(
  project: string, objName: string,
  opts: { xpdl?: string; notes?: string; action?: string } = {},
): Promise<{ ok: boolean; workflow: string; version: number; created: boolean; profile: string }> {
  const res = await api.post('/livepreview/approvals/publish', {
    project, obj_name: objName,
    ...(opts.xpdl ? { xpdl: opts.xpdl } : {}),
    ...(opts.notes ? { notes: opts.notes } : {}),
    // `action` targets ONE action button's approval chain instead of the
    // object's CRUD chain (approval.actions[]).
    ...(opts.action ? { action: opts.action } : {}),
  })
  return res.data
}

/** Version history + in-flight counts for an object's approval workflow. */
export async function approvalsVersions(
  project: string, objName: string, opts: { action?: string } = {},
): Promise<{ ok: boolean; workflow: string | null; active_version: number | null; versions: ApprovalVersionMeta[] }> {
  const res = await api.post('/livepreview/approvals/versions', {
    project, obj_name: objName,
    ...(opts.action ? { action: opts.action } : {}),
  })
  return res.data
}

// One issue from validating a workflow against the engine's executable subset.
export interface ApprovalValidationIssue {
  severity: 'error' | 'warning'
  code: string
  message: string
  where?: string   // offending activity/transition id (for canvas highlight)
}

export interface ApprovalValidationResult {
  ok: boolean
  profile: string
  issues: ApprovalValidationIssue[]
  summary: { errors: number; warnings: number; ok: boolean }
}

/** Validate an object's approval workflow against the engine's executable subset
 *  (one start, an approved end, reachable nodes, assignable approvers, parseable
 *  conditions). Pass `xpdl` to check a graphical document as-is; omit it to check
 *  the linear workflow built from the transaction's levels. */
export async function approvalsValidate(
  project: string, objName: string, opts: { xpdl?: string; action?: string } = {},
): Promise<ApprovalValidationResult> {
  const res = await api.post('/livepreview/approvals/validate', {
    project, obj_name: objName,
    ...(opts.xpdl ? { xpdl: opts.xpdl } : {}),
    ...(opts.action ? { action: opts.action } : {}),
  })
  return res.data
}

// Semantic diff between two workflow versions (executable content only).
export interface ApprovalDiffField { name: string; old: unknown; new: unknown }
export interface ApprovalDiffChangedNode {
  id?: string; name?: string; from?: string; to?: string; on?: string
  fields: ApprovalDiffField[]
}
export interface ApprovalDiffGroup {
  added: Array<Record<string, unknown>>
  removed: Array<Record<string, unknown>>
  changed: ApprovalDiffChangedNode[]
}
export interface ApprovalDiff {
  checksum_changed: boolean
  binding: { changed: boolean; fields: ApprovalDiffField[] }
  participants: ApprovalDiffGroup
  activities: ApprovalDiffGroup
  transitions: ApprovalDiffGroup
}

/** Semantic diff between two published versions of an object's workflow. */
export async function approvalsDiff(
  project: string, objName: string, fromVersion: number, toVersion: number,
): Promise<{ ok: boolean; workflow: string; from_version: number; to_version: number; diff: ApprovalDiff }> {
  const res = await api.post('/livepreview/approvals/diff', {
    project, obj_name: objName, from_version: fromVersion, to_version: toVersion,
  })
  return res.data
}

// ── Phase 3: bulk, analytics, delegation, overrides, SLA, migration ─────────

/** Per-tenant override values for this project's approval conditions (#15a). */
export async function approvalOverrideList(
  project: string, objName = '_approvals',
): Promise<{ ok: boolean; overrides: ApprovalOverride[] }> {
  return await livePreviewExecute(project, objName, 'approval_override_list', {}) as
    { ok: boolean; overrides: ApprovalOverride[] }
}

export async function approvalOverrideSet(
  project: string, name: string, value: string,
  opts: { label?: string; objName?: string } = {},
): Promise<{ ok: boolean; name: string; value: string }> {
  return await livePreviewExecute(project, opts.objName || '_approvals', 'approval_override_set', {
    name, value, ...(opts.label ? { label: opts.label } : {}),
  }) as { ok: boolean; name: string; value: string }
}

export async function approvalOverrideDelete(
  project: string, name: string, objName = '_approvals',
): Promise<{ ok: boolean }> {
  return await livePreviewExecute(project, objName, 'approval_override_delete', { name }) as
    { ok: boolean }
}

export interface ApprovalMigrationResult {
  ok: boolean
  workflow: string
  from_version: number
  to_version: number
  dry_run: boolean
  migrated: number
  migrated_ids: string[]
  skipped: Array<{ id: string; reason: string }>
  pending_total: number
}

/** Migrate an object's in-flight approvals onto a newer published version via
 *  an explicit activity map (super-approver only) (Phase 3 #15b). */
export async function approvalsMigrate(
  project: string, objName: string, fromVersion: number, toVersion: number,
  opts: { activityMap?: Record<string, string>; dryRun?: boolean } = {},
): Promise<ApprovalMigrationResult> {
  const res = await api.post('/livepreview/approvals/migrate', {
    project, obj_name: objName, from_version: fromVersion, to_version: toVersion,
    activity_map: opts.activityMap || {}, dry_run: !!opts.dryRun,
  })
  return res.data
}
